import { isAnyOf } from '@reduxjs/toolkit';
import { lastValueFrom } from 'rxjs';
import Router from 'next/router';

import { getBox } from '../slices/box.slice';
import {
  getCart, getCartError, getCartSuccess,
  updateCart, updateCartError, updateCartSuccess,
  updateCartAttribute, updateCartAttributeError, updateCartAttributeSuccess,
  updateCartPartial, updateCartPartialError, updateCartPartialSuccess,
  updateCartAttributeList, updateCartAttributeListSuccess, updateCartAttributeListError,
  payCart, payCartSuccess, payCartError,
  validateCart, validateCartError, validateCartSuccess,
  addCartProduct, addCartProductSuccess, addCartProductError,
  editCartWineboxSuccess, editCartWineboxError, editCartWinebox,
  addCartBox, addCartBoxSuccess, addCartBoxError,
  deleteCartSuccess, deleteCart, deleteCartError,
  setCartAddress, setCartAddressSuccess, setCartAddressError,
  addOrderToCart, addOrderToCartSuccess, addOrderToCartError,
  getPaymodes, setPaymodes, updateAddressInsideCart, updateAttributeInsideCart
} from '../slices/cart.slice';
import { getShippingModesForCartSuccess, setCheckingIncompatibleProducts } from '../slices/checkout.slice';
import { hidePageProgress, hideProductModal, hideSelectGiftModal, showPageProgress } from '../slices/gui.slice';
import { addApiErrorToast } from '../slices/toast.slice';
import { AppListenerEntry } from '@/redux';
import { CartAttributeKeys, CartModel, CartShippingModel } from '@/models';
import { updateQty } from '@/utils/millesimaUtils';
import { LoggerService } from '@/services';
import { deleteUserAddressSuccess, updateUserAddressSuccess } from '@/redux/slices/user.slice';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { STORE_ID_DEFAULT } from '@/constants/stores';
import { CUSTOMIZATION_STEPS, CUSTOMIZATION_MODES } from '@/constants/customization';
import { PAYMENT_PLATFORMS, STEPS } from '@/constants/checkout';
import { CS_CUSTOM_ERROR } from '@/constants/datalayer';
import { addProductToCartFromCheckout } from '@/redux/thunks/checkout/addProductToCartFromCheckout';
import { deleteProductFromCart } from '@/redux/thunks/deleteProductFromCart';

export const epicGetCart: AppListenerEntry = {
  actionCreator: getCart,
  effect: async (_action: ReturnType<typeof getCart>, { getState, dispatch, extra: { cartService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    const step = getState().checkout.current?.url;
    const isStepHT = [STEPS.REVIEW, STEPS.PAYMODE, STEPS.REPORT].some((s) => step.includes(s));
    try {
      const content = await lastValueFrom(cartService.get(storeId, isStepHT)) as CartModel;
      dispatch(getCartSuccess({ content }));
    } catch (error) {
      dispatch(getCartError({ error }));
      if (error?.error?.errorCode) {
        dispatch(addApiErrorToast({ error }));
      }
    }
  }
};

export const epicUpdateCart: AppListenerEntry = {
  actionCreator: updateCart,
  effect: async ({ payload }: ReturnType<typeof updateCart>, { getState, dispatch, extra: { cartService, datalayerService } }) => {
    const currencyISO = getState().root.storeConf.currencyISO;
    dispatch(showPageProgress());
    try {
      const content = await lastValueFrom(cartService.update(payload.content)) as CartModel;
      // TODO DL
      datalayerService.pushCart(payload.content, currencyISO);
      dispatch(updateCartSuccess({ content }));
    } catch (error) {
      dispatch(updateCartError({ error }));
    } finally {
      dispatch(hidePageProgress());
    }
  }
};

export const epicUpdateCartPartial: AppListenerEntry = {
  actionCreator: updateCartPartial,
  effect: async (
    { payload }: ReturnType<typeof updateCartPartial>,
    { getState, dispatch, extra: { cartService, checkoutService } }
  ) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    const discountCode = getState().cart.content.discountCode;
    const step = getState().checkout.current.url;
    const { content: partial, showToast } = payload;
    try {
      const content = await lastValueFrom(cartService.updatePartial(storeId, partial)) as CartModel;
      if ((partial?.discountCode !== discountCode) && step === STEPS.SHIPPING_MODE) {
        try {
          const shippingsList = await lastValueFrom(checkoutService.getShippingModeForCart(storeId)) as CartShippingModel[];
          dispatch(getShippingModesForCartSuccess({ shippingsList }));
        } catch (error) {
          LoggerService.sendLog(null, 'Error to refresh shippingsList when we add promo code. Error message: ', storeId, step);
        }
      }
      dispatch(updateCartPartialSuccess({ content }));
      dispatch(hideSelectGiftModal());
    } catch (error) {
      dispatch(updateCartPartialError({ error }));
      dispatch(hideSelectGiftModal());
      if (showToast) {
        dispatch(addApiErrorToast({ error }));
      }
    }
  }
};

export const epicUpdateCartAttribute: AppListenerEntry = {
  actionCreator: updateCartAttribute,
  effect: async ({ payload }: ReturnType<typeof updateCartAttribute>, { getState, dispatch, extra: { cartService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    const { attributeName, newValue } = payload;
    try {
      const content = await lastValueFrom(cartService.updateAttribute(storeId, attributeName, newValue)) as CartModel;
      dispatch(updateCartAttributeSuccess({ content }));
    } catch (error) {
      dispatch((updateCartAttributeError({ error })));
    }
  }
};

export const epicUpdateCartAttributeList: AppListenerEntry = {
  actionCreator: updateCartAttributeList,
  effect: async ({ payload }: ReturnType<typeof updateCartAttributeList>, { getState, dispatch, extra: { cartService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    const { attributeList } = payload;
    try {
      const content = await lastValueFrom(cartService.updateAttributeList(storeId, attributeList)) as CartModel;
      dispatch(updateCartAttributeListSuccess({ content }));
    } catch (error) {
      dispatch(updateCartAttributeListError({ error }));
    }
  }
};

export const epicPayCart: AppListenerEntry = {
  actionCreator: payCart,
  effect: async ({ payload }: ReturnType<typeof payCart>, {
    getState, dispatch, extra: { cartService }
  }) => {
    const { cart } = getState();
    const { content } = payload;

    const isAdyen = [
      PAYMENT_PLATFORMS.ADYEN, PAYMENT_PLATFORMS.ADYEN_APPLEPAY, PAYMENT_PLATFORMS.ADYEN_TWINT
    ].includes(
      cart.content?.paiementMode?.action as any
    );

    try {
      let response: any;
      if (isAdyen) {
        response = await lastValueFrom(cartService.validateOrderAdyenWithCart(content));
      } else {
        response = await lastValueFrom(cartService.pay(content));
      }

      const { state, reason, forwardUrl, adyenAction } = response;
      dispatch(payCartSuccess({
        success: { state, reason, forwardUrl, adyenAction }
      }));
    } catch (error) {
      dispatch(payCartError({ error }));
    }
  }
};

export const epicValidate: AppListenerEntry = {
  actionCreator: validateCart,
  effect: async ({ payload }: ReturnType<typeof validateCart>, { dispatch, extra: { cartService } }) => {
    const { content } = payload;
    try {
      const response: any = await lastValueFrom(cartService.validate(content));
      const { state, reason, forwardUrl } = response;
      dispatch(validateCartSuccess({
        success: { state, reason, forwardUrl }
      }));
    } catch (error) {
      dispatch(validateCartError({ error }));
    }
  }
};

// Set the quantity of the product in the cart and remove it if the quantity is 0
export const epicAddCartProduct: AppListenerEntry = {
  actionCreator: addCartProduct,
  effect: async ({ payload }: ReturnType<typeof addCartProduct>, {
    getState, dispatch, extra: { cartService, datalayerService }
  }) => {
    const { root, cart } = getState();
    const { storeId = STORE_ID_DEFAULT } = root.storeConf;
    const { item, qty, custoText, options } = payload;
    const currencyISO = getState().root.storeConf.currencyISO;

    const setQty = (options && options.set === false) ? false : true;
    const showConfirm = (options && options.showConfirm === false) ? false : true;

    // When setQty is false, qty have to be updated if the product already exist in the current box
    const partNumber = custoText ? item + custoText : item;
    const newQuantity = setQty ? qty : updateQty(qty, partNumber, cart.content.products);
    try {
      if (cart.content.products.length === 0) {
        datalayerService.pushEvent('CreateCart');
      }
      const content = await lastValueFrom(cartService.addProduct(storeId, item, newQuantity, custoText)) as CartModel;
      datalayerService.pushCart(content, currencyISO);
      if (content?.products?.length > 0) {
        const lastAdded = content.products.find((p) => p.product.item.partnumber === item);
        if (lastAdded) {
          datalayerService.pushLastAdded(
            'addedToCart',
            lastAdded,
            currencyISO,
            qty
          );
        }
      }
      dispatch(addCartProductSuccess({ content, showConfirm }));
      dispatch(hideProductModal());
    } catch (error) {
      dispatch(addCartProductError({ error }));
    }
  }
};

export const epicEditCartWinebox: AppListenerEntry = {
  actionCreator: editCartWinebox,
  effect: async ({ payload }: ReturnType<typeof editCartWinebox>, { getState, dispatch, extra: { cartService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    const { boxId } = payload;
    try {
      const redirectUrl = await lastValueFrom(cartService.editWinebox(storeId, boxId));
      // Redirecting to winebox categ page
      Router.push(redirectUrl);
      dispatch(editCartWineboxSuccess());
    } catch (error) {
      dispatch(editCartWineboxError({ error }));
    }
  }
};

export const epicAddCartBox: AppListenerEntry = {
  actionCreator: addCartBox,
  effect: async ({ payload }: ReturnType<typeof addCartBox>, { getState, dispatch, extra: { cartService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    const { boxId, qty, options } = payload;
    const showConfirm = (options?.showConfirm === false) ? false : true;
    try {
      const content = await lastValueFrom(cartService.addBox(storeId, boxId, qty)) as CartModel;
      if (options?.redirectToCustomization) {
        Router.push(`/customization/${boxId}/${CUSTOMIZATION_STEPS[0].url}/mode=${CUSTOMIZATION_MODES.PREVIEW}`);
      }
      dispatch(addCartBoxSuccess({ content, showConfirm }));
    } catch (error) {
      dispatch(addCartBoxError({ error }));
    }
  }
};

export const epicBoxAdded: AppListenerEntry = {
  actionCreator: addCartBoxSuccess,
  effect: (_action: ReturnType<typeof addCartBoxSuccess>, { dispatch }) => {
    dispatch(getBox());
  }
};

export const epicDeleteCart: AppListenerEntry = {
  actionCreator: deleteCart,
  effect: async (_action: ReturnType<typeof deleteCart>, { getState, dispatch, extra: { cartService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    try {
      const content = await lastValueFrom(cartService.delete(storeId)) as CartModel;
      dispatch(deleteCartSuccess({ content }));
    } catch (error) {
      dispatch(deleteCartError({ error }));
    }
  }
};

export const epicResetCartOnError: AppListenerEntry = {
  matcher: isAnyOf(
    addCartBoxError,
    addCartProductError,
    deleteCartError,
    payCartError,
    updateCartError,
    updateCartPartialError,
    updateCartAttributeError
  ),
  effect: (_action: ReturnType<any>, { dispatch }) => {
    dispatch(getCart());
  }
};

export const epicSetCartAddress: AppListenerEntry = {
  actionCreator: setCartAddress,
  effect: async ({ payload }: ReturnType<typeof setCartAddress>, { getState, dispatch, extra: { cartService } }) => {
    const { content: address } = payload;
    try {
      const { storage } = getState();
      if (storage?.cartStorage?.length > 0) {
        dispatch(setCheckingIncompatibleProducts());
      }
      const content = await lastValueFrom(cartService.selectAddress(
        address.storeId, address.addressId, address.isBillingMode
      )) as CartModel;
      dispatch(setCartAddressSuccess({ content }));
    } catch (error) {
      dispatch(setCartAddressError({ error }));
    }
  }
};

export const epicAddOrderToCart: AppListenerEntry = {
  actionCreator: addOrderToCart,
  effect: async ({ payload }: ReturnType<typeof addOrderToCart>, { dispatch, extra: { cartService } }) => {
    const { content } = payload;
    try {
      dispatch(showPageProgress());
      const productsNotAdded = await lastValueFrom(cartService.addOrderToCart(content.storeId, content.orderId));
      cartService.addOrderToCartSuccess(productsNotAdded);
      dispatch(addOrderToCartSuccess());
    } catch (error) {
      dispatch(addOrderToCartError({ error }));
    }
  }
};

export const epicAddOrderToCartSuccess: AppListenerEntry = {
  actionCreator: addOrderToCartSuccess,
  effect: async (_action: ReturnType<typeof addOrderToCart>, { dispatch }) => {
    dispatch(getCart());
    dispatch(hidePageProgress());
  }
};

export const epicAddOrderToCartError: AppListenerEntry = {
  actionCreator: addOrderToCartError,
  effect: async (_action: ReturnType<typeof addOrderToCart>, { dispatch }) => {
    dispatch(hidePageProgress());
  }
};

export const epicGetPayModes: AppListenerEntry = {
  actionCreator: getPaymodes,
  effect: async ({ payload }: ReturnType<typeof getPaymodes>, { getState, dispatch, extra: { cartService } }) => {
    const { cart } = getState();
    const { storeId, isApplePayEnabled } = payload;
    try {
      const payModes = await lastValueFrom(cartService.getPaymentModes(storeId, isApplePayEnabled)) as any[];
      if (payModes?.length !== 1 && payModes[0]._id === cart.content.paiementMode?._id) {
        dispatch(setPaymodes({ payModes }));
      } else {
        dispatch(showPageProgress());
        dispatch(setPaymodes({ payModes }));
        dispatch(hidePageProgress());
      }
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicAddCartProductErrorPush: AppListenerEntry = {
  actionCreator: addCartProductError,
  effect: async ({ payload }: ReturnType<typeof addCartProduct>, { extra: { datalayerService } }) => {
    const { error } = payload;
    datalayerService.pushCSCustomError(CS_CUSTOM_ERROR.ADD_PRODUCT, error);
  }
};

export const epicOnDeleteUserAddressIfSelectedSetCartAddress: AppListenerEntry = {
  actionCreator: deleteUserAddressSuccess,
  effect: async ({ payload }: ReturnType<typeof deleteUserAddressSuccess>, { getState, dispatch }) => {
    const { addresses } = payload.user;
    const cart = getState().cart.content;

    if (!cart.shipping) {
      const defaultAddress = addresses.find((a) => a.isDefault);
      dispatch(setCartAddress({ content: { storeId: cart.storeId, addressId: defaultAddress?.idNext, isBillingMode: false } }));
    }
  }
};

export const epicSetCartAttributeWhenUpdatingIt : AppListenerEntry = {
  matcher: (action) => action.type === updateCartAttributeByName.pending.type,
  effect: async (payload, { getState, dispatch }) => {
    const { attributeName, newValue } = payload.meta.arg as { attributeName: CartAttributeKeys, newValue: unknown };
    switch (attributeName) {
      // TODO SMK-3197 : Vérifier si cela pose problème quand il n'y a pas de shippingMode par exemple
      // case 'shippingMode':
      //   console.log('epicSetCartAttributeWhenUpdatingIt :> ', getState().checkout.shippingModes);
      //   const shippingMode = getState().checkout.shippingModes.find(
      //     (mode: CartShippingModel) => mode._id === payload.meta.arg.newValue._id
      //   );
      //   if (shippingMode) {
      //     dispatch(updateAttributeInsideCart({ [attributeName]: shippingMode } as Record<CartAttributeKeys, unknown>));
      //   }
      //   break;
      case 'shipping':
      case 'billing':
        const shippingAddress = getState().user.content.addresses.find((a) => a.idNext === payload.meta.arg.newValue.idNext);
        if (shippingAddress) {
          dispatch(updateAddressInsideCart({ address: shippingAddress, addressType: payload.meta.arg.attributeName }));
        }
        break;
      default:
        dispatch(updateAttributeInsideCart({ [attributeName]: newValue } as Record<CartAttributeKeys, unknown>));
        break;
    }
  }
};

export const epicSetAddressInsideCartWhenUserUpdatesOrCreatesOne: AppListenerEntry = {
  actionCreator: updateUserAddressSuccess,
  effect: async (action: ReturnType<typeof updateUserAddressSuccess>, { dispatch, getState }) => {
    const currentCheckoutStep = getState().checkout.step.label;
    const idToSelect = action.payload.idToSelect;
    if (isObjectEmpty(action.payload.user.addresses) || !idToSelect) {
      return;
    }
    const updatedAddress = action.payload.user.addresses.find((address) => address.idNext === idToSelect);
    if (currentCheckoutStep === STEPS.SHIPPING_ADDRESS) {
      await dispatch(updateCartAttributeByName({ attributeName: 'shipping', newValue: updatedAddress }));
      await dispatch(updateCartAttributeByName({ attributeName: 'billing', newValue: updatedAddress }));
    }
    if (currentCheckoutStep === STEPS.BILLING_ADDRESS) {
      await dispatch(updateCartAttributeByName({ attributeName: 'billing', newValue: updatedAddress }));
    }
  }
};

export const epicSendCartEventToDatalayer: AppListenerEntry = {
  matcher: (action) => updateCartFulfilledTypes.includes(action.type),
  effect: async (
    { payload }, { getState, extra: { datalayerService } }
  ) => {
    const currency = getState().root.storeConf.currencyISO;
    datalayerService.pushCart(payload.content || payload, currency);
  }
};

export const epicSendLastProductToDatalayerOnAddToCart: AppListenerEntry = {
  matcher: (action) => updateCartPendingTypes.includes(action.type),
  effect: async (
    { meta }, { getState, extra: { datalayerService } }
  ) => {
    const { quantity, partnumber } = meta.arg;
    const currency = getState().root.storeConf.currencyISO;
    const cartProducts = getState().cart.content?.products;

    const lastProductAdded = cartProducts.find((product) => {
      const currentPartnumber = product.product.item.partnumber;
      return currentPartnumber === partnumber;
    });

    datalayerService.pushLastAdded(
      !quantity || (parseInt(quantity) < lastProductAdded.quantity) ? 'removedFromCart' : 'addedToCart',
      lastProductAdded.product,
      currency,
      !quantity || (parseInt(quantity) < lastProductAdded.quantity) ? undefined : lastProductAdded.quantity
    );
  }
};

const updateCartPendingTypes = [
  addProductToCartFromCheckout.pending.type,
  deleteProductFromCart.pending.type
];

const updateCartFulfilledTypes = [
  addProductToCartFromCheckout.fulfilled.type,
  deleteProductFromCart.fulfilled.type
];
